import { extend } from 'quasar'
import type { DigitalTwinsFile } from 'src/models'
import { DIGITAL_TWINS_FILE, DIGITAL_TWINS_FILE_VERSION } from 'src/services/constants'

/**
 * validate DigitalTwinsFile, the file has the complete structure
 * @param {DigitalTwinsFile} digitalTwinsFile
 */
export const isValidDigitalTwinsFile = (digitalTwinsFile: DigitalTwinsFile) => {
  if (!digitalTwinsFile?.digitalTwinsFileInfo)
    return { isValid: false, message: 'Has no property digitalTwinsFileInfo' }
  if (!digitalTwinsFile?.digitalTwinsFileInfo?.fileVersion)
    return { isValid: false, message: 'Has no property fileVersion' }
  if (digitalTwinsFile?.digitalTwinsFileInfo?.fileVersion !== DIGITAL_TWINS_FILE_VERSION)
    return {
      isValid: false,
      message: `Has not the correct fileVersion ${DIGITAL_TWINS_FILE_VERSION}`
    }
  if (!digitalTwinsFile?.digitalTwinsGraph)
    return { isValid: false, message: 'Has no property digitalTwinsGraph' }
  if (!digitalTwinsFile?.digitalTwinsGraph?.digitalTwins)
    return { isValid: false, message: 'Has no property digitalTwins' }
  if (!digitalTwinsFile?.digitalTwinsGraph?.relationships)
    return { isValid: false, message: 'Has no property relationships' }
  if (!digitalTwinsFile?.digitalTwinsModels)
    return { isValid: false, message: 'Has no property digitalTwinsModels' }
  return { isValid: true, message: 'DigitalTwinsFile is valid' }
}

/**
 * the DigitalTwinsFile has the correct fileVersion and it contains at least digitalTwins, relationships or digitalTwinsModels
 * @param {DigitalTwinsFile} digitalTwinsFile
 */
export const isNotEmptyDigitalTwinsFile = (digitalTwinsFile: DigitalTwinsFile | undefined) => {
  if (!digitalTwinsFile) return { isValid: false, message: 'DigitalTwinsFile undefined' }
  if (!digitalTwinsFile?.digitalTwinsFileInfo)
    return { isValid: false, message: 'Has no property digitalTwinsFileInfo' }
  if (!digitalTwinsFile?.digitalTwinsFileInfo?.fileVersion)
    return { isValid: false, message: 'Has no property fileVersion' }
  if (digitalTwinsFile?.digitalTwinsFileInfo?.fileVersion !== DIGITAL_TWINS_FILE_VERSION)
    return {
      isValid: false,
      message: `Has not the correct fileVersion ${DIGITAL_TWINS_FILE_VERSION}`
    }
  if (digitalTwinsFile?.digitalTwinsGraph) {
    if (
      digitalTwinsFile?.digitalTwinsGraph?.digitalTwins &&
      digitalTwinsFile?.digitalTwinsGraph?.digitalTwins?.length > 0
    ) {
      return { isValid: true, message: 'DigitalTwinsFile has data' }
    }
    if (
      digitalTwinsFile?.digitalTwinsGraph?.relationships &&
      digitalTwinsFile?.digitalTwinsGraph?.relationships?.length > 0
    ) {
      return { isValid: true, message: 'DigitalTwinsFile has data' }
    }
  }
  if (digitalTwinsFile?.digitalTwinsModels && digitalTwinsFile?.digitalTwinsModels?.length > 0) {
    return { isValid: true, message: 'DigitalTwinsFile has data' }
  }
  return { isValid: false, message: 'DigitalTwinsFile has no data' }
}

/**
 * make valid DigitalTwinsFile, the file has the complete structure
 * @param {DigitalTwinsFile} digitalTwinsFile
 * @returns {DigitalTwinsFile} digitalTwinsFile
 */
export const makeValidDigitalTwinsFile = (
  digitalTwinsFile: DigitalTwinsFile | undefined
): DigitalTwinsFile => {
  if (!digitalTwinsFile) return DIGITAL_TWINS_FILE
  if (!isNotEmptyDigitalTwinsFile(digitalTwinsFile).isValid) return DIGITAL_TWINS_FILE
  const _digitalTwinsFile = extend(true, {}, digitalTwinsFile) as DigitalTwinsFile
  if (!_digitalTwinsFile.digitalTwinsFileInfo) {
    _digitalTwinsFile.digitalTwinsFileInfo = {
      fileVersion: DIGITAL_TWINS_FILE_VERSION
    }
  }
  if (!_digitalTwinsFile.digitalTwinsGraph) {
    _digitalTwinsFile.digitalTwinsGraph = {
      digitalTwins: [],
      relationships: []
    }
  }
  if (!_digitalTwinsFile.digitalTwinsGraph.digitalTwins) {
    _digitalTwinsFile.digitalTwinsGraph.digitalTwins = []
  }
  if (!_digitalTwinsFile.digitalTwinsGraph.relationships) {
    _digitalTwinsFile.digitalTwinsGraph.relationships = []
  }
  if (!_digitalTwinsFile.digitalTwinsModels) {
    _digitalTwinsFile.digitalTwinsModels = []
  }
  return _digitalTwinsFile
}

export type SchemaType =
  | 'string'
  | 'double'
  | 'float'
  | 'boolean'
  | 'integer'
  | 'long'
  | 'dateTime'
  | 'date'
  | 'time'
  | 'object'
  | 'map'

/**
 * Checks whether the value matches the provided schema (type)
 * @param value
 * @param type
 * @returns
 */
export const checkSchema = (value: unknown, type: SchemaType): boolean => {
  const valueType = typeof value
  try {
    switch (type) {
      case 'string':
        return typeof value === 'string'
      case 'double':
      case 'float':
        return typeof value === 'number' && !Number.isNaN(value)
      case 'integer':
      case 'long':
        return typeof value === 'number' && !Number.isNaN(value) && Number.isInteger(value)
      case 'boolean':
        return typeof value === 'boolean'
      case 'object':
      case 'map':
        return typeof value === 'object'
      case 'dateTime':
        if (typeof value !== 'string') return false
        return dateTimeRegex.test(value)
      case 'date':
        if (typeof value !== 'string') return false
        return dateRegex.test(value)
      case 'time':
        if (typeof value !== 'string') return false
        return timeRegex.test(value)
      default:
        return valueType === type
    }
  } catch {
    return false
  }
}

/**
 * try to convert the value to the provided schema (type) if the check did not pass
 * @param value
 * @param type
 * @returns
 */
export const convertToSchema = (
  value: unknown,
  type: string
): string | number | boolean | undefined => {
  try {
    if (type === 'string' && typeof value === 'object') {
      // Object with target string-- > JSON.stringify
      return JSON.stringify(value)
    } else if (['double', 'float'].includes(type) && typeof value === 'string') {
      // String with target double / float-- > parseFloat
      return Number.parseFloat(value)
    } else if (['integer', 'long'].includes(type) && typeof value === 'string') {
      // String with target int-- > parseInt
      return Number.parseInt(value, 10)
    } else if (type === 'string' && typeof value === 'number') {
      // Number with target string-- > `${value}`
      return `${value}`
    } else if (type === 'boolean') {
      if (typeof value === 'number' && (value === 0 || value === 1)) {
        return value !== 0
      } else if (typeof value === 'string') {
        const strValue = value

        if (['true', 'false', '0', '1'].includes(strValue.toLocaleLowerCase())) {
          return !['false', '0'].includes(strValue.toLocaleLowerCase())
        }
      } else if (typeof value === 'boolean') {
        return value
      }
      return undefined
    } else if (type === 'date') {
      return new Date(value as string | number).toISOString().split('T')[0]
    } else if (type === 'dateTime') {
      return new Date(value as string | number).toISOString()
    } else if (type === 'time') {
      return new Date(value as string | number).toISOString().split('T')[1]
    } else {
      return undefined
    }
  } catch {
    return undefined
  }
}

// https://github.com/Azure/digital-twin-model-identifier#validation-regular-expressions

export const regexDtmi =
  // eslint-disable-next-line sonarjs/regex-complexity
  /^dtmi:[A-Za-z](?:\w*[A-Za-z0-9])?(?::[A-Za-z](?:\w*[A-Za-z0-9])?)*(?:;[1-9]\d{0,8}(?:\.[1-9]\d{0,5})?)?$/

export const regexDtmiShort =
  /^dtmi:[A-Za-z](?:\w*[A-Za-z0-9])?(?::[A-Za-z](?:\w*[A-Za-z0-9])?)*;[1-9]\d{0,8}$/

// eslint-disable-next-line sonarjs/slow-regex
export const regexEmail = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/

// debug regex => https://www.debuggex.com/
export const dateTimeRegex = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/i
// allow (no check on ranges) 2000-12-01
export const dateRegex = /^\d{4}-\d\d-\d\d$/i
// allow (check on ranges) 23:59:59, 23:59:59.9, 23:59:59.9999999, 23:59:59Z, 23:59:59.9999999Z, 23:59:59+14:59, 23:59:59-14:59

export const timeRegex =
  /^([0-1]\d|2[0-3]):[0-5]\d:[0-5]\d(\.\d{1,7})?(([+-]([0-1]\d|2[0-3]):[0-5]\d)|Z)?$/i
// allow (check on HH:mm or HH:mm:ss)
export const timeHHmmssRegex = /^([0-1]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/i

// Date or Time optional => Date (ex. 29/09/2023, 2023-09-29 ) Time (ex. 12:00, 29/09/2023 12:00:00, 29/09/2023 12:00.000 ...)
// ex. "29/09/2023 12:00:00".match(...matchDateTimeRegex)
//     (3)['29/09/2023 12:00:00', '29/09/2023', '12:00:00', index: 0, input: '29/09/2023 12:00:00', groups: undefined]
//      DateTimeMatch[0]  => '29/09/2023 12:00:00'
//      DateMatch[1]      => '29/09/2023'
//      TimeMatch[2]      => '12:00:00'

export const matchDateTimeRegex =
  // eslint-disable-next-line sonarjs/regex-complexity, sonarjs/empty-string-repetition
  /^(?:(\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2})?(?:\s|T)?((?:[0-1]\d|2[0-3]):(?:[0-5]\d):?(?:[0-5]\d)?(?:\.\d{1,7})?(?:(?:[+-](?:[0-1]\d|2[0-3]):?:[0-5]\d)|Z)?)?)?$/i

// now used for removing special chars in twinIds
export const removeSpecialChars = /[^A-Za-z0-9\-+%_#*?!(),=@$']/g
// better option for removing special chars in twinIds
export const removeAllSpecialChars = /[`~!@#$%^&*()_|+\\\-=?;:',\s]/g
